import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Modal from "@app/components/modal/modal.component";
import TextArea from "@app/components/textarea";
import { CustomSelect } from "@app/components/select/custom-select";
import ChevronDownArrow from "@app/components/select/chevron-down";
import { genreOptions } from "@app/constants";
import { ThumbnailByAIPayload } from "@app/types";
import { PortalDialogProps } from "@app/services/modal.service";
import { Form, Formik, FormikContextType } from "formik";
import { createRef, useEffect } from "react";
import { aiThumbnailValidationSchema } from "@app/validations";

interface AiThumbnailModalProps {
  bookName: string;
  authorName: string;
  onSubmit: (data: ThumbnailByAIPayload) => void;
}

interface AiThumbnailForm {
  name: string;
  authorName: string;
  mainColor: string;
  description: string;
  genreName: string | number;
}

function AiThumbnailModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<AiThumbnailModalProps>) {
  const formRef = createRef<FormikContextType<AiThumbnailForm>>();

  const onSubmit = (values: AiThumbnailForm) => {
    const payload: ThumbnailByAIPayload = {
      name: values.name.trim(),
      authorName: values.authorName.trim(),
      mainColor: values.mainColor.trim(),
      description: values.description.trim(),
      genreName: values.genreName.toString(),
    };

    if (portalData?.onSubmit) {
      portalData.onSubmit(payload);
    }

    portalDialogRef.close();
  };

  useEffect(() => {
    if (portalData && formRef.current) {
      formRef.current.setValues({
        name: portalData.bookName || "",
        authorName: portalData.authorName || "",
        mainColor: "",
        description: "",
        genreName: "",
      });
    }
  }, [portalData]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[700px] py-7 px-6">
        <div className="text-black font-semibold text-xl mb-2">
          Tạo bìa bằng AI
        </div>
        <div className="text-sm text-gray-600 mb-6">
          Điền thông tin bìa theo ưu cầu của bạn (Lưu ý: Chỉ được tạo 3 lần).
        </div>
        <Formik
          initialValues={{
            name: portalData?.bookName || "",
            authorName: portalData?.authorName || "",
            mainColor: "",
            description: "",
            genreName: "",
          }}
          onSubmit={onSubmit}
          innerRef={formRef}
          validationSchema={aiThumbnailValidationSchema}
        >
          <Form>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên tác phẩm
              </label>
              <FormControl name="name">
                <Input
                  inputClassName="text-[#2C2D33]"
                  placeholder="Tên của tác phẩm sẽ hiển thị trên bìa"
                />
              </FormControl>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên tác giả
              </label>
              <FormControl name="authorName">
                <Input
                  inputClassName="text-[#2C2D33]"
                  placeholder="Tên tác giả, nickname có thể giống hoặc khác với tên tác giả trong tài khoản của bạn"
                />
              </FormControl>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Màu chủ đạo
              </label>
              <FormControl name="mainColor">
                <Input
                  inputClassName="text-[#2C2D33]"
                  placeholder="Màu xanh dương, hoặc màu cam và màu xanh lá"
                />
              </FormControl>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mô tả biểu tượng trên bìa
              </label>
              <FormControl name="description">
                <TextArea
                  textAreaClassName="bg-color border-none outline-none rounded-lg !px-4 !py-3"
                  width="100%"
                  height="auto"
                  rows={4}
                  placeholder="Một cô gái mặc váy ngồi cùng với một chú chó ở trạm xe buýt"
                />
              </FormControl>
            </div>

            <div className="mb-6">
              <FormControl name="genreName">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phong cách bìa
                </label>
                <CustomSelect
                  options={genreOptions}
                  className="bg-color !h-[52px] rounded-lg w-full"
                  optionContainerClassName="mt-4 rounded-b-lg max-h-52 overflow-y-auto w-full"
                  optionClassName="whitespace-nowrap px-4 py-3 border-b border-solid w-full"
                  labelSelectedClassName="black-50 !mx-3"
                  placeholder="Chọn phong cách bìa"
                  arrow={<ChevronDownArrow />}
                  hasBgColorHover
                />
              </FormControl>
            </div>

            <div className="flex justify-center gap-4">
              <Button
                type="button"
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label="Tạo bìa sách"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default AiThumbnailModal;
